import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange, validateRangeAllowZero } from '../../range-validate';
import {
  TermInsuranceCalculatorService, TermInsuranceResult, TERM_INSURANCE_CONSTRAINTS
} from './term-insurance-calculator.service';

@Component({
  selector: 'app-term-insurance-calculator',
  standalone: true,
  imports: [FormsModule],
  providers: [InvestmentChartService],
  templateUrl: './term-insurance-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./term-insurance-calculator.scss']
})
export class TermInsuranceCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(TermInsuranceCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;

  readonly C = TERM_INSURANCE_CONSTRAINTS;
  readonly Math = Math;

  annualIncome = 1_200_000;
  replacementYears = 15;
  outstandingLoans = 2_500_000;
  futureGoals = 2_000_000;
  existingCover = 0;
  existingSavings = 500_000;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     TermInsuranceResult | null = null;

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.annualIncome, this.C.annualIncome.min, this.C.annualIncome.max, 'Annual income');
    if (e1) errors['annualIncome'] = e1;
    const e2 = validateRange(this.replacementYears, this.C.replacementYears.min, this.C.replacementYears.max, 'Income replacement years');
    if (e2) errors['replacementYears'] = e2;
    const e3 = validateRangeAllowZero(this.outstandingLoans, this.C.outstandingLoans.min, this.C.outstandingLoans.max, 'Outstanding loans');
    if (e3) errors['outstandingLoans'] = e3;
    const e4 = validateRangeAllowZero(this.futureGoals, this.C.futureGoals.min, this.C.futureGoals.max, 'Future goals');
    if (e4) errors['futureGoals'] = e4;
    const e5 = validateRangeAllowZero(this.existingCover, this.C.existingCover.min, this.C.existingCover.max, 'Existing cover');
    if (e5) errors['existingCover'] = e5;
    const e6 = validateRangeAllowZero(this.existingSavings, this.C.existingSavings.min, this.C.existingSavings.max, 'Existing savings');
    if (e6) errors['existingSavings'] = e6;
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    if (field === 'replacementYears') this.replacementYears = Math.floor(this.replacementYears || 0);
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = {
      annualIncome: true, replacementYears: true, outstandingLoans: true,
      futureGoals: true, existingCover: true, existingSavings: true
    };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(
      this.annualIncome, Math.floor(this.replacementYears), this.outstandingLoans,
      this.futureGoals, this.existingCover, this.existingSavings
    );
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdown(this.breakdownRef.nativeElement, [
        { label: 'Income Replacement', value: r.incomeReplacement, color: '#3b82f6' },
        { label: 'Outstanding Loans', value: this.outstandingLoans, color: '#ef4444' },
        { label: 'Future Goals', value: this.futureGoals, color: '#f59e0b' }
      ], 'Gross Need');
    }, 60);
  }

  reset(): void {
    this.annualIncome     = 1_200_000;
    this.replacementYears = 15;
    this.outstandingLoans = 2_500_000;
    this.futureGoals      = 2_000_000;
    this.existingCover    = 0;
    this.existingSavings  = 500_000;
    this.formErrors       = {};
    this.touched          = {};
    this.showFormErrors   = false;
    this.calculated       = false;
    this.result           = null;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
